// The Agents view: the daemon's registered agents managed live. Mirrors the
// board's connection lifecycle — open a Connection, show the project title with
// an "Agents" suffix, fetch the agent list, and re-fetch (debounced) whenever
// the daemon pushes an event or the list changes underneath us. Reached by deep
// link (#a=<id>) from Settings, so it's not a top-level tab — the bottom nav
// renders with no active tab.
import { el, clear, isInteracting } from "../dom.js";
import { Connection } from "../relay.js";
import { bottomNav } from "../nav.js";
import { projectTitle } from "../topbar.js";
import type { DaemonEntry } from "../store.js";
import type { Agent } from "../types.js";
import { renderAgents } from "./agents-list.js";

const REFETCH_DEBOUNCE_MS = 500;

export interface AgentsNav {
  onSwitch: () => void;
  onFeed: () => void;
  onBoard: () => void;
  onSettings: () => void;
}

export function renderAgentsView(root: HTMLElement, entry: DaemonEntry, nav: AgentsNav): () => void {
  clear(root);

  const dot = el("span", { class: "status-dot" });
  const banner = el("div", {});
  const list = el("div", {}, [el("div", { class: "empty" }, ["Connecting…"])]);

  root.append(
    el("header", { class: "topbar" }, [
      projectTitle(dot, entry.name, "Agents", nav.onSwitch),
    ]),
    banner,
    list,
    bottomNav({ active: null, onFeed: nav.onFeed, onBoard: nav.onBoard, onSettings: nav.onSettings }),
  );

  let conn: Connection | null = null;
  let stopped = false;
  let refetchTimer = 0;
  let reconnectDelay = 1000;

  const setOnline = (online: boolean) => {
    dot.className = `status-dot ${online ? "online" : "offline"}`;
  };

  const refetch = async () => {
    if (!conn) return;
    try {
      const agents = await conn.api<Agent[]>("GET", "/api/agents");
      clear(list);
      list.append(renderAgents(agents ?? [], {
        api: conn.api.bind(conn),
        onChanged: scheduleRefetch,
      }));
      banner.replaceChildren();
    } catch (e) {
      banner.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
    }
  };

  const scheduleRefetch = () => {
    clearTimeout(refetchTimer);
    refetchTimer = window.setTimeout(() => {
      // Don't rebuild the list out from under someone reading or typing — retry
      // on the next tick so the update still lands once they're done.
      if (isInteracting(list)) return scheduleRefetch();
      void refetch();
    }, REFETCH_DEBOUNCE_MS);
  };

  const connect = async () => {
    if (stopped) return;
    banner.replaceChildren();
    const c = new Connection(entry, {
      onEvent: () => scheduleRefetch(),
      onClose: () => {
        setOnline(false);
        if (stopped) return;
        banner.replaceChildren(el("div", { class: "notice" }, ["Connection lost — reconnecting…"]));
        window.setTimeout(() => void connect(), reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
      },
    });
    try {
      await c.connect();
      conn = c;
      reconnectDelay = 1000;
      setOnline(true);
      await refetch();
    } catch (e) {
      setOnline(false);
      clear(list);
      list.append(el("div", { class: "empty" }, ["Daemon unreachable."]));
      banner.replaceChildren(
        el("div", { class: "error-banner" }, [(e as Error).message]),
        el("div", { class: "actions" }, [el("button", { onclick: () => void connect() }, ["Retry"])]),
      );
    }
  };
  void connect();

  return () => {
    stopped = true;
    clearTimeout(refetchTimer);
    conn?.close();
  };
}
